/**
 * お支払い手続きへの遷移先決定ユーティリティ
 *
 * お問い合わせ詳細画面の「お支払い手続きへ」ボタン押下時の遷移先を、
 * 支払方法コード・支払い済みフラグ・見積もり情報の有無から決定する。
 *
 * Doctor 側 (home_doctor_front/src/pages/worksForm/components/payment/Payment.tsx)
 * の支払方法 → 確認画面マッピングと整合：
 * - cash / bill → CashPaymentConfirm
 * - credit     → CreditCardPaymentForm
 * - postpaid   → PostpaidPaymentConfirm
 */

import type { PaymentMethodCode } from '../data/paymentRepository';

/**
 * 「お支払い手続きへ」ボタン押下時の遷移先パスを決定する。
 *
 * @param inquiryId    お問い合わせID
 * @param paymentMethodCode  支払方法コード（未選択の場合は null/undefined）
 * @param isPaid       既に支払い済みか
 * @param hasEstimateInfo  見積もり情報の有無（フォールバック判定用）
 * @returns 遷移先パス。遷移すべきでない場合は null
 */
export const resolvePaymentNavigatePath = (
  inquiryId: string,
  paymentMethodCode: PaymentMethodCode | null | undefined,
  isPaid: boolean,
  hasEstimateInfo: boolean,
): string | null => {
  // 支払い済みなら hasEstimateInfo の値に関わらず遷移しない（再決済画面への
  // フォールバックを抑制。重複決済・UX上の「再決済を迫る誤解」を防ぐ）
  if (isPaid) return null;

  if (paymentMethodCode) {
    switch (paymentMethodCode) {
      case 'cash':
      case 'bill':
        return `/inquiries/${inquiryId}/cash-payment-confirm`;
      case 'credit':
        return `/inquiries/${inquiryId}/credit-card`;
      case 'postpaid':
        return `/inquiries/${inquiryId}/postpaid-confirm`;
      default: {
        // 将来 PaymentMethodCode に新たな値が追加された場合、コンパイル時に検知する
        // （cash/bill/credit/postpaid 全てを網羅し忘れて再び「ボタン無反応」を出さないため）
        const _exhaustive: never = paymentMethodCode;
        return _exhaustive;
      }
    }
  }
  if (hasEstimateInfo) {
    return `/inquiries/${inquiryId}/payment-method?mode=change`;
  }
  return null;
};
